import { ColumnDef } from "@tanstack/react-table";
import { Visit } from "../types/visit";
import { useUser } from "../../../features/settings/hooks/useUser";
import { Badge } from "../../../components/ui/badge";
import { format } from "date-fns";
import { MoreHorizontal, FileText, Printer, Calendar, Circle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";

interface VisitHistoryColumnProps {
  onViewDetails: (visit: Visit) => void;
  onPrintRecord: (visit: Visit) => void;
}

const statusStyles = {
  completed: {
    color: "text-emerald-700 dark:text-emerald-500",
    bg: "bg-emerald-50 dark:bg-emerald-950",
    border: "border-emerald-200/50 dark:border-emerald-900/50",
    icon: "text-emerald-500 dark:text-emerald-400"
  },
  "in-progress": {
    color: "text-blue-700 dark:text-blue-500",
    bg: "bg-blue-50 dark:bg-blue-950",
    border: "border-blue-200/50 dark:border-blue-900/50",
    icon: "text-blue-500 dark:text-blue-400"
  },
  scheduled: {
    color: "text-amber-700 dark:text-amber-500",
    bg: "bg-amber-50 dark:bg-amber-950",
    border: "border-amber-200/50 dark:border-amber-900/50",
    icon: "text-amber-500 dark:text-amber-400"
  },
  cancelled: {
    color: "text-rose-700 dark:text-rose-500",
    bg: "bg-rose-50 dark:bg-rose-950",
    border: "border-rose-200/50 dark:border-rose-900/50",
    icon: "text-rose-500 dark:text-rose-400"
  }
};

export const columns = ({
  onViewDetails,
  onPrintRecord,
}: VisitHistoryColumnProps): ColumnDef<Visit>[] => [
  {
    accessorKey: "startTime",
    header: "Date & Time",
    cell: ({ row }) => {
      const date = new Date(row.getValue("startTime"));
      return (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Calendar className="w-4 h-4 text-primary/70" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium">{format(date, "MMM d, yyyy")}</span>
            <span className="text-sm text-muted-foreground">{format(date, "h:mm a")}</span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "title",
    header: "Patient",
    cell: ({ row }) => (
      <div className="font-medium">{row.getValue("title")}</div>
    )
  },
  {
    accessorKey: "type",
    header: "Type",
    cell: ({ row }) => {
      const type = row.getValue("type") as string;
      return (
        <Badge 
          variant={type === "outreach" ? "secondary" : "default"}
          className="capitalize font-medium"
        >
          {type}
        </Badge>
      );
    },
  },
  {
    accessorKey: "doctorId",
    header: "Physician",
    cell: ({ row }) => {
      const doctorId = row.getValue("doctorId") as string;
      const { data: physician, isLoading } = useUser(doctorId);
      
      if (isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
      if (!physician) return <div className="text-sm text-muted-foreground">Not found</div>;
      
      return (
        <div className="flex flex-col">
          <span className="font-medium">
            {physician.title} {physician.firstName} {physician.lastName}
          </span>
          {physician.specialization && (
            <span className="text-sm text-muted-foreground">
              {physician.specialization}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as keyof typeof statusStyles;
      const style = statusStyles[status];
      
      return (
        <div className={cn(
          "flex items-center gap-2 w-fit px-2.5 py-1 rounded-full border",
          style.bg,
          style.border
        )}>
          <Circle className={cn("w-2 h-2", style.icon)} />
          <span className={cn("text-sm font-medium capitalize", style.color)}>
            {status}
          </span>
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const visit = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className="hover:bg-primary/10 data-[state=open]:bg-primary/10"
            >
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="w-52 bg-background/80 backdrop-blur-sm"
          >
            <DropdownMenuLabel className="text-muted-foreground">
              Visit Actions
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(visit);
              }}
              className="cursor-pointer hover:bg-primary/10"
            >
              <FileText className="mr-2 h-4 w-4" /> View Details
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={(e) => {
                e.stopPropagation();
                onPrintRecord(visit);
              }}
              className="cursor-pointer hover:bg-primary/10"
            >
              <Printer className="mr-2 h-4 w-4" /> Print Record
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
